import { useCallback, useEffect, useState } from 'react';

import { debounce } from 'lodash';

import { SearchCustomerResult } from '../interfaces/SearchCustomerResult';
import { SolutionType } from '../../shared/constants/user-info/solutionType';
import { getCustomerProfileUrl } from '../utils/getCustomerProfileUrl/getCustomerProfileUrl';
import { goTo } from '../../shared/utils/angularUtils';
import { searchService } from '../services/searchService';

type UseSearchCustomerProps = {
  solutionType: SolutionType;
};

type UseSearchCustomerReturn = {
  inputValue: string;
  options: SearchCustomerResult[];
  loading: boolean;
  onInputChange: (value: string) => void;
  handleSelectCustomer: (customer: SearchCustomerResult | null) => void;
};

export const useSearchCustomer = ({
  solutionType,
}: UseSearchCustomerProps): UseSearchCustomerReturn => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<SearchCustomerResult[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchResults = useCallback(
    debounce(async (query: string) => {
      const trimmed = query.trim();
      if (!trimmed) {
        setOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      const results = await searchService.searchCustomer(trimmed, solutionType);

      setOptions(results);
      setLoading(false);
    }, 400),
    [solutionType],
  );

  useEffect(() => {
    return () => fetchResults.cancel();
  }, [fetchResults]);

  const onInputChange = (value: string) => {
    setInputValue(value);
    fetchResults(value);
  };

  const handleSelectCustomer = (customer: SearchCustomerResult | null) => {
    onInputChange('');

    if (!customer) {
      return;
    }

    const url = getCustomerProfileUrl({
      solutionType,
      entityId: customer.entityId,
      customerId: customer.customerId,
      withHashPrefix: false,
    });
    goTo(url);
  };

  return { inputValue, options, loading, onInputChange, handleSelectCustomer };
};
